import React, { useState, useEffect } from 'react';
import { Clock, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

const getTimeFromString = (timeStr) => {
  if (!timeStr) {
    console.error('Invalid time string:', timeStr);
    return null;
  }

  try {
    const [time, period] = timeStr.trim().split(' ');
    const [hours, minutes] = time.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      console.error('Invalid time format:', timeStr);
      return null;
    }

    let adjustedHours = hours;
    if (period === 'PM' && hours !== 12) {
      adjustedHours += 12;
    } else if (period === 'AM' && hours === 12) {
      adjustedHours = 0;
    }

    return { hour: adjustedHours, minutes };
  } catch (error) {
    console.error('Error parsing time:', error);
    return null;
  }
};

const getNextClass = (classes) => {
  if (!classes) return null;

  const now = new Date();
  const currentDay = ['U', 'M', 'T', 'W', 'R', 'F', 'S'][now.getDay()];
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  let nextClass = null;
  let minTimeDiff = Infinity;
  let hasClassesToday = false;

  console.log('Current day:', currentDay);
  console.log('Current time:', currentHour, ':', currentMinute);
  console.log('Classes:', classes);

  classes.forEach(cls => {
    if (!cls.schedule) {
      console.log('No schedule for class:', cls);
      return;
    }
    
    const [days, timeRange] = cls.schedule.split(' ').reduce((acc, part, index) => {
      if (index === 0) acc[0] = part;
      else acc[1] = (acc[1] ? acc[1] + ' ' : '') + part;
      return acc;
    }, ['', '']);

    if (days.includes(currentDay)) {
      hasClassesToday = true;
      const [startTimeStr, endTimeStr] = timeRange.split('-').map(t => t.trim());
      
      const startTimeObj = getTimeFromString(startTimeStr);
      const endTimeObj = getTimeFromString(endTimeStr);

      if (!startTimeObj || !endTimeObj) {
        console.error('Could not parse time objects:', { startTimeStr, endTimeStr });
        return;
      }

      console.log('Parsed times:', { startTimeObj, endTimeObj });
      
      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();
      const startTime = startTimeObj.hour * 60 + startTimeObj.minutes;
      const endTime = endTimeObj.hour * 60 + endTimeObj.minutes;

      if (currentTime < endTime) {
        const timeDiff = startTime - currentTime;
        const inProgress = currentTime >= startTime;
        // Show seconds only in the last minute before class
        const showSeconds = !inProgress && timeDiff === 0;

        const classInfo = {
          id: cls.id,
          title: cls.title,
          location: cls.location,
          inProgress,
          timeUntilStart: timeDiff,
          timeRemaining: endTime - currentTime,
          showSeconds
        };

        console.log('Class info calculated:', {
          currentTime,
          startTime,
          endTime,
          timeDiff,
          inProgress,
          showSeconds
        });

        if (!nextClass || (timeDiff >= 0 && timeDiff < nextClass.timeUntilStart)) {
          nextClass = classInfo;
        }
      }
    }
  });

  console.log('Final state:', { hasClassesToday, nextClass });

  return !hasClassesToday ? { status: 'no-classes' } 
       : !nextClass ? { status: 'classes-over' }
       : nextClass;
};

const ScheduleBanner = ({ classes, onNavigate }) => {
  const [showBanner, setShowBanner] = useState(true);
  const [nextClass, setNextClass] = useState(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (!classes) return;

    const updateNextClass = () => {
      const next = getNextClass(classes);
      setNextClass(next);
    };

    updateNextClass();
    const intervalTime = nextClass?.showSeconds ? 1000 : 60000;
    console.log('Setting interval:', { showSeconds: nextClass?.showSeconds, intervalTime });
    
    const interval = setInterval(updateNextClass, intervalTime);
    return () => clearInterval(interval);
  }, [classes, nextClass?.showSeconds]);

  if (!classes || !showBanner) return null;

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="py-2 px-4 bg-theme border-b border-theme theme-transition"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div 
          className="flex items-center gap-2 flex-1 cursor-pointer hover:opacity-75 transition-opacity"
          onClick={() => nextClass && !nextClass.status && onNavigate(nextClass.id)}
        >
          <Clock 
            size={16} 
            className={nextClass?.inProgress ? 'text-success' : 'text-accent'}
          />
          {nextClass ? (
            nextClass.status ? (
              <span className="text-accent">
                {nextClass.status === 'no-classes' ? 'No classes today!' : 'All classes finished for today!'}
              </span>
            ) : (
              <div className={`
                flex items-center gap-1
                ${nextClass.inProgress ? 'bg-success/10 px-3 py-1 rounded-full' : ''}
              `}>
                <span className={`
                  font-medium
                  ${nextClass.inProgress ? 'text-success' : 'text-theme'}
                `}>
                  {nextClass.title}
                </span>
                <span className={nextClass.inProgress ? 'text-success' : 'text-accent'}>
                  {nextClass.inProgress ? (
                    `for ${nextClass.timeRemaining} more minute${nextClass.timeRemaining !== 1 ? 's' : ''}`
                  ) : nextClass.timeUntilStart === 0 ? (
                    <>
                      in{' '}
                      <motion.span
                        key={60 - new Date().getSeconds()}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {60 - new Date().getSeconds()}
                      </motion.span>
                      {' '}seconds
                    </>
                  ) : (
                    `in ${nextClass.timeUntilStart >= 60 
                      ? `${Math.floor(nextClass.timeUntilStart / 60)}h ${nextClass.timeUntilStart % 60}m` 
                      : `${nextClass.timeUntilStart}m`}`
                  )}
                </span>
              </div>
            )
          ) : (
            'Loading...'
          )}
        </div>

        <div className="flex items-center gap-4">
          {nextClass && !nextClass.status && (
            <span className="text-sm text-accent">
              {nextClass.location}
            </span>
          )}
          <button
            onClick={() => setShowBanner(false)}
            className="p-1.5 rounded-full hover:bg-primary text-accent transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ScheduleBanner; 